import { useState, useEffect, useMemo, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import HomePage from "@/pages/HomePage";
import AssessmentPage from "@/pages/AssessmentPage";
import ReportPage from "@/pages/ReportPage";
import AdminPage from "@/pages/AdminPage";
import { Toaster } from "@/components/ui/sonner";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Session Context (simplified - no password auth)
const SessionContext = createContext(null);

export const useSession = () => useContext(SessionContext);

// Keep useAuth as alias for backwards compatibility
export const useAuth = () => useContext(SessionContext);

function SessionProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const savedSession = localStorage.getItem("brain360_session");
    if (savedSession) {
      try {
        setSession(JSON.parse(savedSession));
      } catch (e) {
        localStorage.removeItem("brain360_session");
      }
    }
    setLoading(false);
  }, []);

  const startSession = useCallback((sessionData) => {
    setSession(sessionData);
    localStorage.setItem("brain360_session", JSON.stringify(sessionData));
  }, []);

  const clearSession = useCallback(() => {
    setSession(null);
    localStorage.removeItem("brain360_session");
  }, []);

  const updateSession = useCallback((updates) => {
    setSession(prev => {
      const updatedSession = { ...prev, ...updates };
      localStorage.setItem("brain360_session", JSON.stringify(updatedSession));
      return updatedSession;
    });
  }, []);

  const contextValue = useMemo(() => ({ 
    session, 
    startSession, 
    clearSession, 
    updateSession,
    user: session,
    login: startSession,
    logout: clearSession,
    updateUser: updateSession
  }), [session, startSession, clearSession, updateSession]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
}

// Protected Route Component - requires active session with assessment
function ProtectedRoute({ children }) {
  const { session } = useSession();
  
  if (!session || !session.current_assessment_id) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Admin Route Component
function AdminRoute({ children }) {
  const { session } = useSession();
  
  if (!session || session.user_type !== "admin") {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <SessionProvider>
          <Routes>
            {/* Home page - user information form (landing page) */}
            <Route path="/" element={<HomePage />} />
            
            {/* Assessment page - requires active session */}
            <Route 
              path="/assessment/:assessmentId" 
              element={
                <ProtectedRoute>
                  <AssessmentPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Report page - requires active session */}
            <Route 
              path="/report/:assessmentId" 
              element={
                <ProtectedRoute>
                  <ReportPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Admin dashboard - requires admin session */}
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              } 
            />
          </Routes>
        </SessionProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
